/**
 * useRealtimeTable — Subscribe to Postgres Changes via Supabase Realtime
 *
 * Reusable hook that subscribes to INSERT, UPDATE, and/or DELETE events on a
 * table, scoped to the current organization. When an event fires, the provided
 * callback is invoked with the payload so the consumer can update local state
 * (e.g. invalidate a React Query cache, append to a list, update a counter).
 *
 * The subscription lifecycle is tied to component mount/unmount to prevent
 * connection leaks.
 *
 * @example
 * ```tsx
 * useRealtimeTable({
 *   table: "attendance",
 *   events: ["INSERT"],
 *   filter: `organization_id=eq.${organizationId}`,
 *   onEvent: () => queryClient.invalidateQueries({ queryKey: ["event-checked-in"] }),
 * });
 * ```
 *
 * @see Supabase Adoption Plan — Wave 1C
 */
import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrgId } from "@/hooks/useOrganization";
import { logger } from "@/lib/logger";
import type { RealtimePostgresChangesPayload } from "@supabase/supabase-js";

type RealtimeEvent = "INSERT" | "UPDATE" | "DELETE";

let realtimeSubscriptionSequence = 0;

function nextRealtimeSubscriptionId() {
  realtimeSubscriptionSequence += 1;
  return realtimeSubscriptionSequence;
}

interface UseRealtimeTableOptions<T extends Record<string, unknown> = Record<string, unknown>> {
  /** The table to subscribe to (must be in the supabase_realtime publication). */
  table: string;
  /** Which change events to listen for. Defaults to all three. */
  events?: RealtimeEvent[];
  /**
   * Optional Postgres filter expression for server-side filtering.
   * Example: `event_id=eq.${eventId}` or `status=eq.critical`
   * Only rows matching this filter will trigger the callback.
   */
  filter?: string;
  /**
   * Callback invoked when a matching event arrives.
   * The payload includes the event type and the new/old row data.
   */
  onEvent: (payload: RealtimePostgresChangesPayload<T>) => void;
  /** Set to false to temporarily disable the subscription. Defaults to true. */
  enabled?: boolean;
  /** Schema to listen on. Defaults to "public". */
  schema?: string;
}

export function useRealtimeTable<T extends Record<string, unknown> = Record<string, unknown>>(
  options: UseRealtimeTableOptions<T>,
) {
  const {
    table,
    events = ["INSERT", "UPDATE", "DELETE"],
    filter,
    onEvent,
    enabled = true,
    schema = "public",
  } = options;

  const orgId = useOrgId();

  // Keep a stable ref to onEvent so we don't re-subscribe on every render
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!enabled || !orgId) return;

    // Supabase Realtime reuses channels by topic in newer clients. Keep each
    // hook subscription on its own topic so concurrent consumers can register
    // postgres_changes handlers before subscribing.
    const channelBaseName = `org:${orgId}:${schema}:${table}`;
    const channelName = `${channelBaseName}:sub:${nextRealtimeSubscriptionId()}`;

    logger.debug("[Realtime] Subscribing", { channelName: channelBaseName, channelTopic: channelName, events, filter });

    const channel = supabase.channel(channelName);

    for (const event of events) {
      const listenConfig: {
        event: typeof event;
        schema: string;
        table: string;
        filter?: string;
      } = { event, schema, table };

      // Add filter if provided — server-side filtering reduces network traffic
      if (filter) {
        listenConfig.filter = filter;
      }

      channel.on(
        "postgres_changes" as const,
        listenConfig,
        (payload: RealtimePostgresChangesPayload<T>) => {
          onEventRef.current(payload);
        },
      );
    }

    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let retryCount = 0;
    // Native iOS can be slower to establish WebSocket connections after
    // backgrounding — allow more retries with exponential backoff.
    const MAX_RETRIES = 5;

    channel.subscribe((status) => {
      if (status === "SUBSCRIBED") {
        retryCount = 0; // reset on success
        logger.debug("[Realtime] Subscribed", { channelName: channelBaseName, channelTopic: channelName });
      } else if (status === "CHANNEL_ERROR") {
        logger.warn("[Realtime] Channel error", { channelName: channelBaseName, channelTopic: channelName });
      } else if (status === "TIMED_OUT") {
        logger.warn("[Realtime] Subscription timed out", { channelName: channelBaseName, channelTopic: channelName, retryCount });
        // Auto-retry on timeout (common on iOS after navigation/backgrounding)
        if (retryCount < MAX_RETRIES) {
          retryCount += 1;
          retryTimer = setTimeout(() => {
            logger.debug("[Realtime] Retrying subscription", { channelName: channelBaseName, channelTopic: channelName, attempt: retryCount });
            channel.subscribe();
          }, 2_000 * retryCount); // 2s, 4s backoff
        }
      }
    });

    // Cleanup: remove channel on unmount to prevent connection leaks
    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      logger.debug("[Realtime] Unsubscribing", { channelName: channelBaseName, channelTopic: channelName });
      supabase.removeChannel(channel);
    };
    // Re-subscribe when table, orgId, filter, or enabled changes.
    // events is serialized to avoid unnecessary re-subscribes from array identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, orgId, filter, enabled, schema, events.join(",")]);
}
